import OfferCountdown from './OfferCountdown';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Zap, Crown, Gift, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Pre-select the higher-value yearly plan so more users land on it.
  const [focusedPlan, setFocusedPlan] = useState('premium_yearly');
  // Monthly/Yearly billing toggle (defaults to yearly).
  const [billingCycle, setBillingCycle] = useState('yearly');
  // Promo code (e.g. WELCOME20 from the abandoned-cart email).
  const [promo, setPromo] = useState(null);
  const [promoPct, setPromoPct] = useState(0);
  const [promoExpires, setPromoExpires] = useState(null);
  
  useEffect(() => {
    loadPlans();
    if (user) {
      loadSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  
  const loadPlans = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/payments/plans`);
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error(language === 'es' ? 'Error al cargar planes' : 'Error loading plans');
    }
  };
  
  const loadSubscription = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/payments/subscription`, {
        withCredentials: true
      });
      setSubscription(data.subscription);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };
  
  const handleStripeCheckout = async (planId) => {
    if (!user) {
      toast.error(language === 'es' ? 'Inicia sesión primero' : 'Please login first');
      navigate('/login');
      return;
    }
    
    setLoading(true);
    setSelectedPlan(planId);
    
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payments/stripe/create-checkout`,
        {
          plan_id: planId,
          promo_code: promo || undefined,
          success_url: `${window.location.origin}/payment/success`,
          cancel_url: `${window.location.origin}/pricing`
        },
        { withCredentials: true }
      );
      
      // Remember this attempt so we can offer a retry if the user bails.
      try { localStorage.setItem('lumera_pending_upgrade', JSON.stringify({ planId, provider: 'stripe', ts: Date.now() })); } catch (e) {}
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating checkout');
      setLoading(false);
      setSelectedPlan(null);
    }
  };
  
  const handlePayPalCheckout = async (planId) => {
    if (!user) {
      toast.error(language === 'es' ? 'Inicia sesión primero' : 'Please login first');
      navigate('/login');
      return;
    }
    
    setLoading(true);
    setSelectedPlan(planId);
    
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payments/paypal/create-order`,
        {
          plan_id: planId,
          promo_code: promo || undefined,
          success_url: `${window.location.origin}/payment/success`,
          cancel_url: `${window.location.origin}/pricing`
        },
        { withCredentials: true }
      );
      
      // Remember this attempt so we can offer a retry if the user bails.
      try { localStorage.setItem('lumera_pending_upgrade', JSON.stringify({ planId, provider: 'paypal', ts: Date.now() })); } catch (e) {}
      // Redirect to PayPal
      window.location.href = data.approval_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating order');
      setLoading(false);
      setSelectedPlan(null);
    }
  };
  
  // Surface a promo from ?promo= (incl. anonymous visitors) only if the backend says it's valid for them.
  useEffect(() => {
    const promoParam = new URLSearchParams(window.location.search).get('promo');
    if (!promoParam) return;
    axios.get(`${BACKEND_URL}/api/payments/promo-check?code=${encodeURIComponent(promoParam)}`, { withCredentials: true })
      .then(({ data }) => { if (data.valid) { setPromo(data.code); setPromoPct(data.discount_pct); setPromoExpires(data.expires_at); } })
      .catch(() => {});
  }, [user]);

  // If the retry banner / cart email sent us here with ?resume=<plan>&provider=<p>&promo=<code>.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const resume = params.get('resume');
    const provider = params.get('provider');
    const promoParam = params.get('promo');
    if (resume) {
      // strip the query so a refresh won't re-trigger
      window.history.replaceState({}, '', '/pricing');
      if (promoParam) {
        // Discount applies — surface it and pre-focus the plan; let the user confirm.
        setFocusedPlan(resume);
      } else if (provider === 'paypal') {
        handlePayPalCheckout(resume);
      } else {
        handleStripeCheckout(resume);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleStartTrial = async () => {
    if (!user) {
      toast.error(language === 'es' ? 'Inicia sesión primero' : 'Please login first');
      navigate('/login');
      return;
    }
    setLoading(true);
    setSelectedPlan('trial');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payments/start-trial`,
        { source: 'pricing' },
        { withCredentials: true },
      );
      toast.success(data.message || (language === 'es' ? '¡Prueba iniciada!' : 'Free trial started!'));
      await loadSubscription();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'No se pudo iniciar la prueba' : 'Could not start trial'));
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free':
        return <Gift className="w-8 h-8 text-blue-500" />;
      case 'premium':
      case 'premium_yearly':
        return <Zap className="w-8 h-8 text-purple-500" />;
      case 'lifetime':
        return <Crown className="w-8 h-8 text-amber-500" />;
      default:
        return <Gift className="w-8 h-8" />;
    }
  };
  
  const isCurrentPlan = (planId) => {
    return subscription && subscription.plan_type === planId;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {language === 'es' ? 'Planes y Precios' : 'Plans & Pricing'}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {language === 'es'
              ? 'Elige el plan perfecto para tu estudio bíblico'
              : 'Choose the perfect plan for your Bible study journey'}
          </p>
        </div>
        
        {/* Current Subscription Banner */}
        {subscription && (
          <div className="max-w-2xl mx-auto mb-8">
            <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-300 dark:border-purple-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {language === 'es' ? 'Plan Actual' : 'Current Plan'}
                    </p>
                    <p className="text-2xl font-bold">{subscription.plan_name}</p>
                    {subscription.expires_at && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {language === 'es' ? 'Válido hasta' : 'Valid until'}{' '}
                        {new Date(subscription.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Check className="w-12 h-12 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Promo banner */}
        {promo && (
          <div className="max-w-2xl mx-auto mb-6" data-testid="promo-banner">
            <div className="flex items-center justify-center gap-2 rounded-full bg-green-600 text-white px-4 py-2.5 text-sm font-semibold shadow">
              <Check className="w-4 h-4" />
              {language === 'es'
                ? `Código ${promo} aplicado — ${promoPct}% de descuento en tu plan`
                : `Code ${promo} applied — ${promoPct}% off your plan`}
            </div>
          </div>
        )}

        {/* Billing cycle toggle (defaults to yearly) */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex items-center rounded-full border border-border bg-card p-1" data-testid="billing-toggle">
            <button
              onClick={() => { setBillingCycle('monthly'); setFocusedPlan('premium'); }}
              data-testid="billing-monthly"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${billingCycle === 'monthly' ? 'bg-purple-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {language === 'es' ? 'Mensual' : 'Monthly'}
            </button>
            <button
              onClick={() => { setBillingCycle('yearly'); setFocusedPlan('premium_yearly'); }}
              data-testid="billing-yearly"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-purple-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {language === 'es' ? 'Anual' : 'Yearly'}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${billingCycle === 'yearly' ? 'bg-white/25 text-white' : 'bg-green-100 text-green-700'}`}>
                −17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.filter((plan) => {
            // Show Free & Lifetime always; show the Premium plan matching the toggle.
            if (plan.id === 'premium') return billingCycle === 'monthly';
            if (plan.id === 'premium_yearly') return billingCycle === 'yearly';
            return true;
          }).map((plan) => {
            const isCurrent = isCurrentPlan(plan.id);
            const isPopular = plan.id === 'premium_yearly' || plan.id === 'premium';
            const isFree = plan.price === 0;
            const isFocused = focusedPlan === plan.id && !isFree && !isCurrent;
            
            return (
              <Card
                key={plan.id}
                onClick={() => !isFree && !isCurrent && setFocusedPlan(plan.id)}
                data-testid={`plan-card-${plan.id}`}
                className={`relative transition-all ${!isFree && !isCurrent ? 'cursor-pointer' : ''} ${
                  isPopular
                    ? 'border-purple-500 dark:border-purple-400 shadow-lg shadow-purple-500/20'
                    : ''
                } ${isFocused ? 'ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-slate-900 scale-[1.02]' : ''} ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
              >
                {isPopular && !isFocused && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-purple-500 hover:bg-purple-600">
                      {language === 'es' ? 'Más Popular' : 'Most Popular'}
                    </Badge>
                  </div>
                )}

                {isFocused && (
                  <div className="absolute -top-4 right-4" data-testid={`plan-recommended-${plan.id}`}>
                    <Badge className="bg-purple-600 hover:bg-purple-700">
                      {language === 'es' ? '✓ Recomendado' : '✓ Recommended'}
                    </Badge>
                  </div>
                )}
                
                {isCurrent && (
                  <div className="absolute -top-4 right-4">
                    <Badge className="bg-green-500">
                      {language === 'es' ? 'Activo' : 'Active'}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-8 pt-8">
                  <div className="flex justify-center mb-4">
                    {getPlanIcon(plan.id)}
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="mt-4">
                    {!isFree && promoPct > 0 ? (
                      <>
                        <span className="mr-2 text-xl text-muted-foreground line-through" data-testid={`plan-original-price-${plan.id}`}>
                          ${plan.price}
                        </span>
                        <span className="text-4xl font-bold text-foreground" data-testid={`plan-discounted-price-${plan.id}`}>
                          ${(Math.round(plan.price * (100 - promoPct)) / 100).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-foreground">
                        ${plan.price}
                      </span>
                    )}
                    {!isFree && (
                      <span className="text-muted-foreground">
                        {plan.id === 'lifetime'
                          ? (language === 'es' ? ' pago único' : ' one-time')
                          : '/' + (plan.id.includes('yearly') ? (language === 'es' ? 'año' : 'year') : (language === 'es' ? 'mes' : 'mo'))}
                      </span>
                    )}
                  </CardDescription>
                  {!isFree && promoPct > 0 && (
                    <div className="mt-2 flex justify-center" data-testid={`plan-promo-tag-${plan.id}`}>
                      <Badge className="bg-amber-500 hover:bg-amber-600 text-white">
                        {promoPct}% off · {promo}
                      </Badge>
                    </div>
                  )}
                  {!isFree && promoPct > 0 && promoExpires && (
                    <OfferCountdown expiresAt={promoExpires} language={language} testid={`plan-offer-countdown-${plan.id}`} />
                  )}
                  {plan.id === 'premium_yearly' && (
                    <div className="mt-2 flex items-center justify-center gap-2" data-testid="yearly-savings-badge">
                      <Badge className="bg-green-600 hover:bg-green-700 text-white">
                        {language === 'es' ? 'Ahorra 17%' : 'Save 17%'}
                      </Badge>
                      <span className="text-xs text-muted-foreground line-through">$59.88</span>
                      <span className="text-xs font-medium text-green-600">
                        {language === 'es' ? '≈ 2 meses gratis' : '≈ 2 months free'}
                      </span>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Features */}
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter className="flex flex-col gap-2 pb-8">
                  {isFree ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      disabled
                    >
                      {language === 'es' ? 'Plan Actual' : 'Current Plan'}
                    </Button>
                  ) : isCurrent ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      disabled
                    >
                      {language === 'es' ? 'Plan Activo' : 'Active Plan'}
                    </Button>
                  ) : (
                    <>
                      {/* 15-day free trial (Premium monthly only) */}
                      {plan.id === 'premium' && !subscription && (
                        <>
                          <Button
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={handleStartTrial}
                            disabled={loading}
                            data-testid="start-trial-btn"
                          >
                            {loading && selectedPlan === 'trial' ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {language === 'es' ? 'Iniciando...' : 'Starting...'}
                              </>
                            ) : (
                              <>
                                <Gift className="w-4 h-4 mr-2" />
                                {language === 'es' ? 'Prueba gratis de 15 días' : 'Start 15-day Free Trial'}
                              </>
                            )}
                          </Button>
                          <p className="text-[11px] text-center text-muted-foreground -mt-1">
                            {language === 'es' ? 'Sin tarjeta · Luego $4.99/mes' : 'No card required · then $4.99/mo'}
                          </p>
                        </>
                      )}

                      {/* Stripe Payment */}
                      <Button
                        className="w-full"
                        onClick={() => handleStripeCheckout(plan.id)}
                        disabled={loading}
                      >
                        {loading && selectedPlan === plan.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {language === 'es' ? 'Procesando...' : 'Processing...'}
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            {language === 'es' ? 'Pagar con Stripe' : 'Pay with Stripe'}
                          </>
                        )}
                      </Button>
                      
                      {/* PayPal Payment */}
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => handlePayPalCheckout(plan.id)}
                        disabled={loading}
                      >
                        {loading && selectedPlan === `${plan.id}-paypal` ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {language === 'es' ? 'Procesando...' : 'Processing...'}
                          </>
                        ) : (
                          <>
                            <img
                              src="https://www.paypalobjects.com/webstatic/icon/pp258.png"
                              alt="PayPal"
                              className="w-4 h-4 mr-2"
                            />
                            {language === 'es' ? 'Pagar con PayPal' : 'Pay with PayPal'}
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
        
        {/* Lumera + Slidecon — now unified under every paid plan */}
        <div className="mt-12 max-w-3xl mx-auto" data-testid="bundle-tier-card">
          <Card className="border-2 border-fuchsia-400/60 bg-gradient-to-br from-fuchsia-50 via-violet-50 to-amber-50 dark:from-fuchsia-950/30 dark:via-violet-950/30 dark:to-amber-950/30 shadow-xl">
            <CardHeader className="text-center">
              <div className="flex justify-center items-center gap-2 mb-2">
                <span className="text-xs font-bold tracking-widest px-2 py-1 rounded-full bg-fuchsia-600 text-white" data-testid="bundle-tier-included-badge">
                  {language === 'es' ? 'AHORA INCLUIDO' : 'NOW INCLUDED'}
                </span>
                <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                  {language === 'es' ? 'En todos los planes pagos' : 'In every paid plan'}
                </span>
              </div>
              <CardTitle className="text-2xl bg-gradient-to-r from-fuchsia-600 to-violet-700 bg-clip-text text-transparent">
                {language === 'es' ? 'Lumera + Slidecon, una sola suscripción' : 'Lumera + Slidecon, one subscription'}
              </CardTitle>
              <CardDescription className="mt-2 text-base">
                {language === 'es'
                  ? 'Estudio bíblico en Lumera + diapositivas profesionales en Slidecon. Sin cargos extra: cualquier plan Premium ahora incluye Slidecon Pro.'
                  : "Bible study in Lumera + professional slide decks in Slidecon. No extra fee — any Premium plan now includes Slidecon Pro."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="text-sm space-y-2 max-w-md mx-auto">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-fuchsia-600" />
                  {language === 'es' ? 'Cuenta Slidecon Pro vinculada automáticamente' : 'Slidecon Pro account linked automatically'}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-fuchsia-600" />
                  {language === 'es' ? 'Cualquier conversación → diapositivas en un toque' : 'Any conversation → slides in one tap'}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-fuchsia-600" />
                  {language === 'es' ? 'Plantillas de presentación dominicales' : 'Sunday-ready presentation templates'}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-fuchsia-600" />
                  {language === 'es' ? 'Sin pago separado, sin código de canje' : 'No separate billing, no redemption code'}
                </li>
              </ul>
              {!subscription && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="default"
                    className="bg-gradient-to-r from-fuchsia-600 to-violet-700 text-white hover:from-fuchsia-700 hover:to-violet-800"
                    data-testid="bundle-tier-upgrade-btn"
                    onClick={() => {
                      // scroll the pricing grid into view so user picks a plan
                      document
                        .querySelector('[data-testid="factbook-results"], .grid.grid-cols-1')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  >
                    {language === 'es' ? 'Elige un plan' : 'Choose a plan'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 max-w-3xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">
                {language === 'es' ? '🔒 Pago Seguro' : '🔒 Secure Payment'}
              </CardTitle>
              <CardDescription className="text-center">
                {language === 'es'
                  ? 'Todos los pagos son procesados de forma segura mediante Stripe y PayPal. Tu información está protegida con encriptación de nivel bancario.'
                  : 'All payments are securely processed through Stripe and PayPal. Your information is protected with bank-level encryption.'}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
